import { useEffect } from "react";
import { usePlayer } from "../app/services";
import { useStore } from "../lib/store";
import { formatClock } from "../lib/time";

/** The transport along the bottom. Space plays and pauses, ⌥→ and ⌥← skip, as on the Mac. */
export function PlayerBar() {
  const { engine } = usePlayer();
  const state = useStore(engine);
  const current = state.blocks[state.index] ?? null;
  const progress = state.duration > 0 ? Math.min(1, state.position / state.duration) : 0;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable]")) return;
      if (event.code === "Space") {
        event.preventDefault();
        void engine.togglePause();
      } else if (event.altKey && event.key === "ArrowRight") {
        void engine.next();
      } else if (event.altKey && event.key === "ArrowLeft") {
        void engine.previous();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [engine]);

  if (state.status === "blocked") {
    return (
      <footer className="player-bar">
        <button type="button" className="primary wide" onClick={() => void engine.togglePause()}>
          ▶ Click to start playback — the browser needs a click before it plays sound
        </button>
      </footer>
    );
  }

  return (
    <footer className="player-bar">
      <div className="transport">
        <button type="button" aria-label="Previous" onClick={() => void engine.previous()}>
          ⏮
        </button>
        <button
          type="button"
          className="play"
          aria-label={state.status === "playing" ? "Pause" : "Play"}
          disabled={!current}
          onClick={() => void engine.togglePause()}
        >
          {state.status === "playing" ? "⏸" : "▶"}
        </button>
        <button type="button" aria-label="Next" onClick={() => void engine.next()}>
          ⏭
        </button>
      </div>
      <div className="track-info">
        <span className="title">
          {current
            ? current.kind === "track"
              ? `${current.title} — ${current.artist}`
              : `Host · ${current.title}`
            : "Radiola"}
        </span>
        <div className="scrubber">
          <span>{formatClock(state.position)}</span>
          <div className="bar">
            <div style={{ width: `${progress * 100}%` }} />
          </div>
          <span>{formatClock(state.duration)}</span>
        </div>
      </div>
      <span className="status muted">{STATUS[state.status]}</span>
    </footer>
  );
}

const STATUS: Record<string, string> = {
  idle: "",
  loading: "Loading…",
  playing: "",
  paused: "Paused",
  waiting: "Waiting for more",
  blocked: "",
  ended: "Ended",
};
